const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://techiohisab.com/api";

const buildUrl = (path, params = {}) => {
  const safeParams = Object.entries(params || {}).filter(
    ([, value]) => value !== undefined && value !== null && value !== ""
  );
  const query = new URLSearchParams(safeParams).toString();
  return `${API_BASE_URL}${path}${query ? `?${query}` : ""}`;
};

const rtoAPI = {
  async getRtoOrders(params = {}) {
    try {
      const response = await fetch(buildUrl("/rto/orders", params));
      if (!response.ok) throw new Error("Failed to fetch RTO orders");
      return await response.json();
    } catch (error) {
      console.error("Error fetching RTO orders:", error);
      throw error;
    }
  },

  async getRtoSummary(params = {}) {
    try {
      const response = await fetch(buildUrl("/rto/summary", params));
      if (!response.ok) throw new Error("Failed to fetch RTO summary");
      return await response.json();
    } catch (error) {
      console.error("Error fetching RTO summary:", error);
      throw error;
    }
  },

  async getCourierStats(params = {}) {
    try {
      const response = await fetch(buildUrl("/rto/courier-stats", params));
      if (!response.ok) throw new Error("Failed to fetch courier stats");
      return await response.json();
    } catch (error) {
      console.error("Error fetching courier stats:", error);
      throw error;
    }
  },

  async getFraudZones(params = {}) {
    try {
      const response = await fetch(buildUrl("/rto/fraud-zones", params));
      if (!response.ok) throw new Error("Failed to fetch fraud zones");
      return await response.json();
    } catch (error) {
      console.error("Error fetching fraud zones:", error);
      throw error;
    }
  },

  async getMarketplaceSplit(params = {}) {
    try {
      const response = await fetch(buildUrl("/rto/marketplace-split", params));
      if (!response.ok) throw new Error("Failed to fetch marketplace split");
      return await response.json();
    } catch (error) {
      console.error("Error fetching marketplace split:", error);
      throw error;
    }
  },

  async getLossTrend(params = {}) {
    try {
      const response = await fetch(buildUrl("/rto/loss-trend", params));
      if (!response.ok) throw new Error("Failed to fetch loss trend");
      return await response.json();
    } catch (error) {
      console.error("Error fetching loss trend:", error);
      throw error;
    }
  },

  async verifyRtoOrder(orderId, verificationData) {
    try {
      const response = await fetch(`${API_BASE_URL}/rto/verify/${orderId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(verificationData),
      });
      if (!response.ok) throw new Error("Failed to verify RTO order");
      return await response.json();
    } catch (error) {
      console.error("Error verifying RTO order:", error);
      throw error;
    }
  },

  async updateVerification(orderId, verificationData) {
    return this.verifyRtoOrder(orderId, verificationData);
  },

  async bulkVerifyRtoOrders(bulkData) {
    try {
      const response = await fetch(`${API_BASE_URL}/rto/bulk-verify`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bulkData),
      });
      if (!response.ok) throw new Error("Failed to bulk verify RTO orders");
      return await response.json();
    } catch (error) {
      console.error("Error bulk verifying RTO orders:", error);
      throw error;
    }
  },

  async saveScanEntries(rows) {
    try {
      const response = await fetch(`${API_BASE_URL}/rto/scan-entries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ rows }),
      });
      if (!response.ok) throw new Error("Failed to save scan entries");
      return await response.json();
    } catch (error) {
      console.error("Error saving scan entries:", error);
      throw error;
    }
  },

  async convertToRto(orderId, type) {
    try {
      const response = await fetch(`${API_BASE_URL}/rto/convert/${orderId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ type }),
      });
      if (!response.ok) throw new Error("Failed to convert order to RTO/DTO");
      return await response.json();
    } catch (error) {
      console.error("Error converting order to RTO/DTO:", error);
      throw error;
    }
  },

  async shareOrders({ orderIds, destination }) {
    try {
      const response = await fetch(`${API_BASE_URL}/rto/share`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ orderIds, destination }),
      });
      if (!response.ok) throw new Error("Failed to share orders");
      return await response.json();
    } catch (error) {
      console.error("Error sharing orders:", error);
      throw error;
    }
  },

  async getTransferRequests(destination) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/rto/transfers?destination=${encodeURIComponent(destination)}`
      );
      if (!response.ok) throw new Error("Failed to fetch transfer requests");
      return await response.json();
    } catch (error) {
      console.error("Error fetching transfer requests:", error);
      throw error;
    }
  },

  async getTransferByOrderId(orderId, destination = "manufacture") {
    try {
      const requests = await this.getTransferRequests(destination);
      const targetOrderId = String(orderId || "").trim();
      if (!targetOrderId) return null;
      return requests.find((item) => String(item?.orderId || "").trim() === targetOrderId) || null;
    } catch (error) {
      console.error("Error fetching transfer by order id:", error);
      throw error;
    }
  },

  async acceptTransfer(requestId, destination) {
    try {
      const response = await fetch(`${API_BASE_URL}/rto/transfers/${requestId}/accept`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ destination }),
      });
      if (!response.ok) throw new Error("Failed to accept transfer");
      return await response.json();
    } catch (error) {
      console.error("Error accepting transfer:", error);
      throw error;
    }
  },

  async rejectTransfer(requestId, destination) {
    try {
      const response = await fetch(`${API_BASE_URL}/rto/transfers/${requestId}/reject`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ destination }),
      });
      if (!response.ok) throw new Error("Failed to reject transfer");
      return await response.json();
    } catch (error) {
      console.error("Error rejecting transfer:", error);
      throw error;
    }
  },

  async acceptAllTransfers(destination) {
    try {
      const response = await fetch(`${API_BASE_URL}/rto/transfers/accept-all`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ destination }),
      });
      if (!response.ok) throw new Error("Failed to accept all transfers");
      return await response.json();
    } catch (error) {
      console.error("Error accepting all transfers:", error);
      throw error;
    }
  },

  async shareToManufacturing(orderId) {
    return this.shareOrders({ orderIds: [orderId], destination: "manufacture" });
  },
};

export default rtoAPI;
